import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cloneElement, forwardRef, isValidElement, useState, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/shadcn/tooltip';

import {
  actionPresets,
  type ActionPreset,
  type ActionPresetKey,
  type TableActionTone,
  type TableActionVariant,
} from './presets';

/**
 * Variantes de color del botón. Dos tonos coexisten:
 *
 * - `soft` (default): pill tintado — bg `{variant}/10`, borde `{variant}/25`,
 *   icono en el color semántico. Visible en idle, no compite con la fila
 *   porque el contraste es bajo. Equivalente al chip "Aprobar/Rechazar/
 *   En Revisión/Ampliar/Cancelar" del Figma.
 *
 * - `solid`: relleno fuerte reservado para CTAs de creación al estilo
 *   "+ Nuevo / + ítem" (mismo gradiente que `<Button variant="gradient">`).
 *   Las acciones de fila usan `soft` para no competir con ese CTA.
 *   Para success/warning/destructive usa el color sólido + foreground blanco.
 */
export const tableActionButtonVariants = cva(
  cn(
    'inline-flex size-9 items-center justify-center rounded-md border',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ),
  {
    variants: {
      tone: {
        soft: '',
        solid: 'shadow-sm hover:opacity-95',
      },
      variant: {
        neutral: '',
        info: '',
        success: '',
        warning: '',
        destructive: '',
        primary: '',
      },
    },
    compoundVariants: [
      // Soft — pill tintado, color visible en idle.
      {
        tone: 'soft',
        variant: 'neutral',
        className: 'border-border bg-muted/50 text-muted-foreground hover:bg-muted',
      },
      {
        tone: 'soft',
        variant: 'info',
        className: 'border-info/25 bg-info/10 text-info hover:bg-info/15',
      },
      {
        tone: 'soft',
        variant: 'success',
        className: 'border-success/25 bg-success/10 text-success hover:bg-success/15',
      },
      {
        tone: 'soft',
        variant: 'warning',
        className: 'border-warning/25 bg-warning/10 text-warning hover:bg-warning/15',
      },
      {
        tone: 'soft',
        variant: 'destructive',
        className:
          'border-destructive/25 bg-destructive/10 text-destructive hover:bg-destructive/15',
      },
      {
        tone: 'soft',
        variant: 'primary',
        className: 'border-primary/25 bg-primary/10 text-primary hover:bg-primary/15',
      },
      // Solid — relleno fuerte. Primary e info reusan el gradiente del botón
      // "Nuevo" para mantener una sola identidad azul en toda la app.
      {
        tone: 'solid',
        variant: 'primary',
        className:
          'border-transparent bg-[linear-gradient(94deg,#2158F3_0%,#1A46D4_100%)] text-white',
      },
      {
        tone: 'solid',
        variant: 'info',
        className:
          'border-transparent bg-[linear-gradient(94deg,#2158F3_0%,#1A46D4_100%)] text-white',
      },
      {
        tone: 'solid',
        variant: 'neutral',
        className: 'border-border bg-foreground text-background',
      },
      {
        tone: 'solid',
        variant: 'success',
        className: 'border-transparent bg-success text-success-foreground',
      },
      {
        tone: 'solid',
        variant: 'warning',
        className: 'border-transparent bg-warning text-warning-foreground',
      },
      {
        tone: 'solid',
        variant: 'destructive',
        className: 'border-transparent bg-destructive text-destructive-foreground',
      },
    ],
    defaultVariants: { variant: 'neutral', tone: 'soft' },
  },
);

export interface ConfirmConfig {
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface CommonProps {
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
  onClick?: () => void;
  /**
   * Texto del AlertDialog de confirmación. Solo aplica si el preset tiene
   * `requiresConfirmation: true` o si se pasa explícitamente. Si se omite
   * y el preset lo exige, se muestra un texto genérico aceptable.
   */
  confirm?: ConfirmConfig;
}

interface PresetVariant extends CommonProps {
  /** Preset canónico definido en `presets.ts`. */
  action: ActionPresetKey;
  /** Sobrescribe el `tone` del preset (ej. forzar solid en un caller puntual). */
  tone?: TableActionTone;
  /** Si `true`, el child reemplaza el `<button>` (Slot). El icono del preset
   *  se inyecta automáticamente dentro del child — el caller no lo duplica. */
  asChild?: boolean;
  /** Cuando `asChild`, el elemento root (típicamente un `<Link>`). */
  children?: ReactNode;
  icon?: never;
  variant?: never;
  tooltip?: never;
}

interface ExplicitVariant extends CommonProps, VariantProps<typeof tableActionButtonVariants> {
  /** Icono lucide (u otro). El componente lo escala automáticamente a 16×16. */
  icon: ReactNode;
  /** Tooltip visible al hover y aria-label por default. */
  tooltip: string;
  /** Si `true`, el child reemplaza el `<button>` (Slot). Ideal para Link. */
  asChild?: boolean;
  children?: ReactNode;
  action?: never;
}

export type TableActionButtonProps = PresetVariant | ExplicitVariant;

/**
 * Botón de acción para filas de tabla. Solo icono, con tooltip integrado
 * obligatorio (vía Radix Tooltip + shadcn). Variantes semánticas vía cva.
 *
 * @example Preset canónico
 * ```tsx
 * <TableActionButton action="edit" onClick={() => abrir(item)} />
 * ```
 *
 * @example Confirmación destructiva
 * ```tsx
 * <TableActionButton
 *   action="deactivate"
 *   onClick={() => mutate(item.id)}
 *   confirm={{
 *     title: '¿Desactivar item?',
 *     description: `"${item.nombre}" dejará de aparecer.`,
 *   }}
 * />
 * ```
 *
 * @example Explícito con asChild para Link
 * ```tsx
 * <TableActionButton variant="info" tooltip="Ver detalle" icon={<Eye />} asChild>
 *   <Link to="/proyectos/$id" params={{ id: String(item.id) }} />
 * </TableActionButton>
 * ```
 */
export const TableActionButton = forwardRef<HTMLButtonElement, TableActionButtonProps>(
  function TableActionButton(props, ref) {
    const { className, disabled, onClick, confirm, 'aria-label': ariaLabelProp } = props;
    const [confirmOpen, setConfirmOpen] = useState(false);

    const config = resolveConfig(props);
    const { variant, tone, tooltip, iconNode, requiresConfirmation, asChild, children } = config;

    const ariaLabel = ariaLabelProp ?? tooltip;
    const shouldConfirm = requiresConfirmation || Boolean(confirm);

    const triggerClick = () => {
      if (disabled) return;
      if (shouldConfirm) {
        setConfirmOpen(true);
        return;
      }
      onClick?.();
    };

    const buttonClassName = cn(tableActionButtonVariants({ variant, tone }), className);

    // En modo asChild inyectamos el icono dentro del child via cloneElement
    // para que el caller no tenga que repetir `<Icon />` al usar presets.
    const trigger = asChild ? (
      <Slot
        className={buttonClassName}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
      >
        {isValidElement(children) ? cloneElement(children, undefined, iconNode) : children}
      </Slot>
    ) : (
      <button
        ref={ref}
        type="button"
        className={buttonClassName}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={triggerClick}
      >
        {iconNode}
      </button>
    );

    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent size="sm">{tooltip}</TooltipContent>
        </Tooltip>

        {shouldConfirm && (
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{confirm?.title ?? '¿Confirma esta acción?'}</AlertDialogTitle>
                <AlertDialogDescription>
                  {confirm?.description ??
                    'Esta acción puede afectar a otros usuarios del sistema.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{confirm?.cancelLabel ?? 'Cancelar'}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    setConfirmOpen(false);
                    onClick?.();
                  }}
                  className={
                    variant === 'destructive'
                      ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                      : undefined
                  }
                >
                  {confirm?.confirmLabel ?? tooltip}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </>
    );
  },
);

type ResolvedConfig = {
  variant: TableActionVariant;
  tone: TableActionTone;
  tooltip: string;
  iconNode: ReactNode;
  requiresConfirmation: boolean;
  asChild: boolean;
  children: ReactNode;
};

function resolveConfig(props: TableActionButtonProps): ResolvedConfig {
  if ('action' in props && props.action) {
    const preset: ActionPreset = actionPresets[props.action];
    const Icon = preset.icon;
    return {
      variant: preset.variant,
      tone: props.tone ?? preset.tone ?? 'soft',
      tooltip: preset.tooltip,
      iconNode: <Icon aria-hidden="true" />,
      requiresConfirmation: Boolean(preset.requiresConfirmation),
      asChild: Boolean(props.asChild),
      children: props.children ?? null,
    };
  }
  return {
    variant: props.variant ?? 'neutral',
    tone: props.tone ?? 'soft',
    tooltip: props.tooltip,
    iconNode: props.icon,
    requiresConfirmation: false,
    asChild: Boolean(props.asChild),
    children: props.children,
  };
}
